import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Button } from '../components/primitives';
import { Field as FormField } from '../components/field';
import { InlineStatus } from '../components/feedback';
import {
  AuthCard,
  AuthNote,
  AuthShell,
  Field,
  MIN_PASSWORD,
  PwToggle,
  passwordProblem,
  type PasswordProblem,
} from '../components/auth-ui';
import { useAuth } from '../lib/auth';

// Every form here is a real <form> with `required` fields: the browser blocks an
// empty submit and says which field, so no submit button is silently disabled
// (its 0.65-opacity "off" state was hard to tell from "on", SH-13). A failure
// is a message under the field it is about, tied to it and announced.

// The quiet second action under a form ("Back to sign in").
const BackToSignIn = ({ onClick }: { onClick: () => void }) => (
  <Button
    variant="ghost"
    size="sm"
    full
    icon="chevronLeft"
    onClick={onClick}
    style={{ marginTop: 'var(--hf-space-4)' }}
  >
    Back to sign in
  </Button>
);

// ── Sign in (password) + forgot-password request ─────────────────────────────
export function LoginScreen() {
  const { startEmailOtp, startResetOtp, configured } = useAuth();
  const [mode, setMode] = useState<'signin' | 'forgot'>('signin');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();

  const submitSignin = async (e: FormEvent) => {
    e.preventDefault();
    if (busy) return;
    setError(undefined);
    setBusy(true);
    const { error } = await startEmailOtp(email.trim(), password);
    setBusy(false);
    setError(error);
    // on success the app swaps to <OtpScreen> via auth.otpEmail
  };

  const submitForgot = async (e: FormEvent) => {
    e.preventDefault();
    if (busy) return;
    setError(undefined);
    setBusy(true);
    const { error } = await startResetOtp(email.trim());
    setBusy(false);
    setError(error);
    // on success the app swaps to <OtpScreen> (reset mode) via auth.otpEmail
  };

  const switchTo = (next: 'signin' | 'forgot') => {
    setMode(next);
    setError(undefined);
  };

  if (mode === 'forgot') {
    return (
      <AuthShell>
        <AuthCard
          title="Reset your password"
          sub="Enter your work email and we’ll send a 6-digit code. Verify it and you can set a new password right away."
          onSubmit={submitForgot}
        >
          <Field
            label="Email"
            type="email"
            value={email}
            onChange={setEmail}
            placeholder="you@hyprflow.com"
            autoComplete="email"
            error={error}
            required
            autoFocus
          />
          <Button
            variant="primary"
            size="xl"
            full
            type="submit"
            loading={busy}
            iconRight="arrowRight"
            style={{ marginTop: 'var(--hf-space-5)' }}
          >
            {busy ? 'Sending…' : 'Email me a code'}
          </Button>
          <BackToSignIn onClick={() => switchTo('signin')} />
        </AuthCard>
      </AuthShell>
    );
  }

  return (
    <AuthShell>
      <AuthCard
        title="Sign in to your workspace"
        sub="Welcome back — pick up where your team left off."
        onSubmit={submitSignin}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--hf-space-4)' }}>
          <Field
            label="Email"
            type="email"
            value={email}
            onChange={setEmail}
            placeholder="you@hyprflow.com"
            autoComplete="email"
            required
          />
          {/* A sign-in failure is about the pair, and is said under the last of
              the two. Words for a placeholder: a row of bullets read as a
              password already filled in (SH-13). */}
          <Field
            label="Password"
            type={showPw ? 'text' : 'password'}
            value={password}
            onChange={setPassword}
            placeholder="Your password"
            autoComplete="current-password"
            error={error}
            required
            trailing={<PwToggle shown={showPw} onClick={() => setShowPw((s) => !s)} />}
          />
          {!configured && (
            <InlineStatus tone="warning">
              Auth not configured — set VITE_SUPABASE_URL / ANON_KEY.
            </InlineStatus>
          )}
        </div>

        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            margin: 'var(--hf-space-2) 0 var(--hf-space-4)',
          }}
        >
          <Button variant="ghost" size="sm" onClick={() => switchTo('forgot')}>
            Forgot password?
          </Button>
        </div>

        <Button
          variant="primary"
          size="xl"
          full
          type="submit"
          loading={busy}
          iconRight="arrowRight"
        >
          {busy ? 'Verifying…' : 'Continue'}
        </Button>

        <AuthNote
          style={{
            marginTop: 'var(--hf-space-4)',
            fontSize: 'var(--hf-text-xs)',
            textAlign: 'center',
          }}
        >
          We’ll email you a one-time code to finish signing in.
        </AuthNote>
      </AuthCard>

      <AuthNote
        style={{
          marginTop: 'var(--hf-space-5)',
          fontSize: 'var(--hf-text-sm)',
          textAlign: 'center',
        }}
      >
        Accounts are created by your HR team. Need access? Ask your administrator. Invited but your
        link stopped working? Use “Forgot password” above.
      </AuthNote>
    </AuthShell>
  );
}

// ── Step 2: enter the 6-digit code emailed after the password ────────────────
const OTP_LEN = 6;
const RESEND_SECONDS = 30;

export function OtpScreen({ email }: { email: string }) {
  const { verifyEmailOtp, resendOtp, cancelOtp, otpMode } = useAuth();
  const isReset = otpMode === 'reset';
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const [cooldown, setCooldown] = useState(RESEND_SECONDS);
  const [resending, setResending] = useState(false);
  const [resent, setResent] = useState(false);
  const submittedFor = useRef<string | null>(null);

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  const verify = async (value: string) => {
    if (busy || value.length !== OTP_LEN) return;
    submittedFor.current = value;
    setError(undefined);
    setResent(false);
    setBusy(true);
    const { error } = await verifyEmailOtp(email, value);
    setBusy(false);
    if (error) {
      setError(error);
      setCode('');
      // A failed try is not "already sent": the failure may have been the
      // network, and the same six digits typed again sat there unanswered.
      submittedFor.current = null;
    }
    // on success the app swaps to the workspace via the new session
  };

  // Typed or pasted, the box keeps the digits and nothing else: "123 456" from
  // the email, or the whole line the code came in, lands as the six digits and
  // is sent. No maxLength, which would cut a spaced paste short before this
  // runs.
  const onChange = (raw: string) => {
    const digits = raw.replace(/\D/g, '').slice(0, OTP_LEN);
    setCode(digits);
    if (error) setError(undefined);
    if (digits.length === OTP_LEN && submittedFor.current !== digits) void verify(digits);
  };

  const resend = async () => {
    if (cooldown > 0 || resending) return;
    setResending(true);
    setError(undefined);
    setResent(false);
    const { error } = await resendOtp(email);
    setResending(false);
    if (error) return setError(error);
    setResent(true);
    setCooldown(RESEND_SECONDS);
  };

  return (
    <AuthShell>
      <AuthCard
        title={isReset ? 'Verify your email' : 'Enter your code'}
        sub={
          <>
            We emailed a 6-digit code to{' '}
            <strong style={{ color: 'var(--hf-ink-2)', overflowWrap: 'anywhere' }}>{email}</strong>.{' '}
            {isReset ? 'Enter it to set a new password.' : 'It expires shortly.'}
          </>
        }
        onSubmit={(e) => {
          e.preventDefault();
          void verify(code);
        }}
      >
        {/* Field's own frame, at the code's size: TextInput is one size, and six
            digits read at a glance are the whole job of this screen. */}
        <FormField label="6-digit code" error={error}>
          {(control) => (
            <div className="hf-field" style={{ display: 'flex', height: 56 }}>
              <input
                {...control}
                inputMode="numeric"
                autoComplete="one-time-code"
                pattern={`[0-9]{${OTP_LEN}}`}
                title={`The ${OTP_LEN} digits from the email`}
                required
                value={code}
                onChange={(e) => onChange(e.target.value)}
                placeholder="000000"
                autoFocus
                style={{
                  flex: 1,
                  minWidth: 0,
                  padding: 0,
                  background: 'transparent',
                  border: 0,
                  textAlign: 'center',
                  fontSize: 'var(--hf-text-3xl)',
                  fontFamily: 'var(--hf-font-mono)',
                  letterSpacing: '0.42em',
                  textIndent: '0.42em',
                  color: 'var(--hf-ink)',
                }}
              />
            </div>
          )}
        </FormField>

        <Button
          variant="primary"
          size="xl"
          full
          type="submit"
          loading={busy}
          iconRight="arrowRight"
          style={{ marginTop: 'var(--hf-space-5)' }}
        >
          {busy ? 'Verifying…' : isReset ? 'Verify & continue' : 'Verify & sign in'}
        </Button>

        {/* A resend used to say nothing: the countdown restarting was the only
            sign that anything had been sent. */}
        {resent && (
          <div style={{ marginTop: 'var(--hf-space-4)' }}>
            <InlineStatus tone="success">A new code is on its way to your inbox.</InlineStatus>
          </div>
        )}

        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 'var(--hf-space-2)',
            marginTop: 'var(--hf-space-4)',
          }}
        >
          <Button variant="ghost" size="sm" icon="chevronLeft" onClick={() => void cancelOtp()}>
            Use a different account
          </Button>
          <Button
            variant="ghost"
            size="sm"
            loading={resending}
            disabled={cooldown > 0}
            onClick={() => void resend()}
          >
            {resending ? 'Sending…' : cooldown > 0 ? `Resend in ${cooldown}s` : 'Resend code'}
          </Button>
        </div>
      </AuthCard>
    </AuthShell>
  );
}

// ── Recovery: set a new password after clicking the reset link ───────────────
export function ResetPasswordScreen() {
  const { updatePassword, signOut } = useAuth();
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<PasswordProblem | null>(null);
  const [done, setDone] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (busy) return;
    const problem = passwordProblem(password, confirm);
    setError(problem);
    if (problem) return;
    setBusy(true);
    const { error } = await updatePassword(password);
    setBusy(false);
    if (error) setError({ on: 'password', text: error });
    else setDone(true);
    // updatePassword clears `recovery`; the app then routes to the workspace
  };

  if (done) {
    return (
      <AuthShell>
        <AuthCard
          title="Password updated"
          sub="Your password has been changed. You can sign in with it next time."
        >
          <Button variant="primary" size="xl" full iconRight="arrowRight" onClick={signOut}>
            Continue to sign in
          </Button>
        </AuthCard>
      </AuthShell>
    );
  }

  return (
    <AuthShell>
      <AuthCard
        title="Choose a new password"
        sub={`Pick a strong password you don’t use elsewhere — at least ${MIN_PASSWORD} characters.`}
        onSubmit={submit}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--hf-space-4)' }}>
          <Field
            label="New password"
            type={showPw ? 'text' : 'password'}
            value={password}
            onChange={setPassword}
            placeholder={`At least ${MIN_PASSWORD} characters`}
            autoComplete="new-password"
            error={error?.on === 'password' ? error.text : undefined}
            required
            autoFocus
            trailing={<PwToggle shown={showPw} onClick={() => setShowPw((s) => !s)} />}
          />
          <Field
            label="Confirm password"
            type={showPw ? 'text' : 'password'}
            value={confirm}
            onChange={setConfirm}
            placeholder="The same password again"
            autoComplete="new-password"
            error={error?.on === 'confirm' ? error.text : undefined}
            required
          />
        </div>

        <Button
          variant="primary"
          size="xl"
          full
          type="submit"
          loading={busy}
          iconRight="arrowRight"
          style={{ marginTop: 'var(--hf-space-5)' }}
        >
          {busy ? 'Updating…' : 'Update password'}
        </Button>
        {/* Escape hatch so a stray recovery state can't trap the user. */}
        <BackToSignIn onClick={signOut} />
      </AuthCard>
    </AuthShell>
  );
}
